import { useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { foundation } from '../../ui';
import NoteCollapse, {
  useCollapsePresence, prefersReducedMotion, NOTE_OPEN_MS, NOTE_CLOSE_MS,
} from './NoteCollapse';

const { fontSize, fontWeight, fontFamily, color } = foundation;

export { prefersReducedMotion };

/** Split "lead · tail" so the tail can render in regular weight. */
function splitLine(text) {
  const i = String(text).indexOf(' · ');
  return i < 0 ? [text, null] : [text.slice(0, i), text.slice(i)];
}

/**
 * The ONE line under a MoneyInput (KAN-1061): the amber price warning, or the
 * brand-blue "Maximum $99,999.99" line when a keystroke was refused at the cap.
 * KAN-1066: it stays mounted through its exit and opens/closes with a height +
 * opacity tween (NoteCollapse), so the box above never moves and what is below
 * glides. Reduced motion = instant.
 */
export default function MoneyInputNote({ id, tone, text, show = true }) {
  const { mounted, expanded } = useCollapsePresence(show);
  const [last, setLast] = useState({ text, tone });
  if (show && (text !== last.text || tone !== last.tone)) setLast({ text, tone });

  if (!mounted) return null;
  const shown = show ? text : last.text;
  const isMax = (show ? tone : last.tone) === 'max';
  const [lead, tail] = splitLine(shown);
  const Icon = isMax ? Info : AlertTriangle;
  return (
    <NoteCollapse open={expanded} testId="money-input-note-collapse">
      <div style={{ paddingTop: foundation.spacePx[1], display: 'flex' }}>
        <span
          id={id}
          {...(isMax ? { role: 'status', 'aria-live': 'polite' } : null)}
          data-testid={isMax ? 'money-input-max-note' : 'money-input-warning-note'}
          style={{ ...baseStyle, ...(isMax ? maxStyle : warnStyle) }}
        >
          <Icon size={12} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />
          <span style={{ minWidth: 0 }}>
            {lead}
            {tail ? <span style={{ fontWeight: fontWeight.normal }}>{tail}</span> : null}
          </span>
        </span>
      </div>
    </NoteCollapse>
  );
}

/**
 * The "Maximum $…" line when a ROW (not the box) shows it - the web extra rows'
 * message line (KAN-1066). Opacity-only fade, so nothing moves; renders nothing
 * once faded out. `style` places the wrapper (width / alignment under the price).
 */
export function MaxNoteSlot({ text, style }) {
  const { mounted, expanded } = useCollapsePresence(Boolean(text));
  const [last, setLast] = useState(text);
  if (text && text !== last) setLast(text);
  if (!mounted) return null;
  const reduced = prefersReducedMotion();
  const ms = text ? NOTE_OPEN_MS : NOTE_CLOSE_MS;
  return (
    <span
      role="status"
      aria-live="polite"
      data-testid="money-input-max-note"
      data-slot="message-line"
      style={{
        ...baseStyle, ...maxStyle, ...style,
        opacity: expanded ? 1 : 0,
        transition: reduced ? 'none' : `opacity ${ms}ms ease`,
      }}
    >
      <Info size={12} strokeWidth={2} aria-hidden style={{ flexShrink: 0 }} />
      <span style={{ minWidth: 0 }}>{text || last}</span>
    </span>
  );
}

const baseStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  alignSelf: 'flex-start',
  fontFamily: fontFamily.sans,
};
const warnStyle = {
  fontSize: fontSize.xs,
  fontWeight: fontWeight.medium,
  color: color.semantic.warningText,
  // May wrap onto a second line on a narrow field - never clipped.
  maxWidth: '100%',
};
// Same size and weight as the amber line (founder, KAN-1068: the two notes are one
// family, only the colour differs). It may wrap under a narrow box, never clip.
const maxStyle = {
  fontSize: fontSize.xs,
  fontWeight: fontWeight.medium,
  color: color.brand.text,
  maxWidth: '100%',
};
